import { useMemo, useState } from "react";
import { Copy, ExternalLink, Code, FileCode, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { badgeUrl, API } from "../lib/api";

const VARIANTS = [
  { id: "pill", label: "Pill" },
  { id: "square", label: "Square" },
  { id: "minimal", label: "Score only" },
];

const THEMES = [
  { id: "dark", label: "Dark" },
  { id: "light", label: "Light" },
];

const SIZES = [
  { id: "sm", label: "Small" },
  { id: "md", label: "Medium" },
  { id: "lg", label: "Large" },
];

const FORMATS = [
  { id: "html", label: "HTML", icon: Code },
  { id: "markdown", label: "Markdown", icon: FileCode },
  { id: "react", label: "React", icon: Sparkles },
];

export default function BadgeBuilder({ report }) {
  const reportUrl = `${window.location.origin}/report/${report.domain}`;
  const [variant, setVariant] = useState("pill");
  const [theme, setTheme] = useState("dark");
  const [size, setSize] = useState("md");
  const [format, setFormat] = useState("html");
  const [copied, setCopied] = useState(false);

  const imgUrl = useMemo(
    () => badgeUrl(report.domain, { variant, theme, size }),
    [report.domain, variant, theme, size],
  );

  const snippet = useMemo(() => {
    const alt = `PrivacyLens TrustScore for ${report.domain}`;
    if (format === "markdown") {
      return `[![${alt}](${imgUrl})](${reportUrl})`;
    }
    if (format === "react") {
      return `<a href="${reportUrl}" target="_blank" rel="noopener noreferrer">
  <img src="${imgUrl}" alt="${alt}" />
</a>`;
    }
    return `<a href="${reportUrl}" target="_blank" rel="noopener"><img src="${imgUrl}" alt="${alt}" /></a>`;
  }, [format, imgUrl, reportUrl, report.domain]);

  const copy = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Could not copy — select & copy manually");
    }
  };

  const previewBg = theme === "light" ? "bg-[#F8F9FA]" : "bg-[#1A1A1A]";

  return (
    <section className="border-b border-black" data-testid="badge-builder">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14 grid lg:grid-cols-12 gap-10">
        {/* Left: intro */}
        <div className="lg:col-span-4">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-3">
            / RiskBadge™ Studio
          </div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.02]">
            Wear your score with pride.
          </h2>
          <p className="text-[#475569] mt-4 leading-relaxed">
            Customize, preview, and copy a live RiskBadge — auto-updates every
            time {report.domain} is re-scanned, no JavaScript required, drops
            into any page.
          </p>
          <ul className="text-sm text-[#475569] mt-6 space-y-2">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
              <span>SVG · scales crisp on any screen</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
              <span>Cached 5 min · cheap on traffic</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
              <span>Color reflects current TrustScore</span>
            </li>
          </ul>
        </div>

        {/* Right: builder */}
        <div className="lg:col-span-8 space-y-5">
          {/* Preview */}
          <div className="border border-black bg-white p-6">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-3">
              Live preview · {variant} · {theme} · {size}
            </div>
            <div
              className={`${previewBg} border border-[#E2E8F0] p-10 flex items-center justify-center transition-colors`}
            >
              {/* Cache-bust the preview so option changes re-paint immediately */}
              <img
                src={imgUrl}
                alt={`PrivacyLens TrustScore for ${report.domain}`}
                data-testid="badge-preview-img"
              />
            </div>
          </div>

          {/* Option groups */}
          <div className="grid sm:grid-cols-3 gap-4">
            <OptionGroup
              label="Style"
              options={VARIANTS}
              value={variant}
              onChange={setVariant}
              testidPrefix="variant"
            />
            <OptionGroup
              label="Theme"
              options={THEMES}
              value={theme}
              onChange={setTheme}
              testidPrefix="theme"
            />
            <OptionGroup
              label="Size"
              options={SIZES}
              value={size}
              onChange={setSize}
              testidPrefix="size"
            />
          </div>

          {/* Format tabs */}
          <div className="border border-black bg-[#0A0A0A] text-white">
            <div className="flex border-b border-white/15">
              {FORMATS.map((f) => {
                const Icon = f.icon;
                const active = format === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFormat(f.id)}
                    className={`px-4 py-3 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-2 border-r border-white/15 last:border-r-0 transition-colors ${
                      active
                        ? "bg-[#0047FF] text-white"
                        : "text-white/70 hover:text-white hover:bg-white/5"
                    }`}
                    data-testid={`format-tab-${f.id}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {f.label}
                  </button>
                );
              })}
              <div className="ml-auto pr-2 flex items-center">
                <button
                  type="button"
                  onClick={() => copy(snippet, `${format.toUpperCase()} snippet`)}
                  className="inline-flex items-center gap-2 bg-[#0047FF] hover:bg-[#0033CC] px-3 py-1.5 text-xs font-mono uppercase tracking-widest"
                  data-testid="copy-badge-snippet"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
            <pre
              className="p-5 text-[12px] font-mono leading-relaxed text-white/90 overflow-x-auto whitespace-pre-wrap break-all"
              data-testid="badge-snippet-code"
            >
              {snippet}
            </pre>
          </div>

          {/* Quick utilities */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => copy(reportUrl, "Report URL")}
              className="pl-btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2"
              data-testid="copy-report-url"
            >
              <Copy className="w-4 h-4" /> Copy report URL
            </button>
            <a
              href={imgUrl}
              target="_blank"
              rel="noreferrer"
              className="pl-btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2"
              data-testid="open-badge-svg"
            >
              <ExternalLink className="w-4 h-4" /> Open SVG in new tab
            </a>
            <a
              href={`${API}/badge/${report.domain}.svg`}
              target="_blank"
              rel="noreferrer"
              className="pl-btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2 font-mono text-xs"
              data-testid="raw-svg-link"
            >
              <ExternalLink className="w-4 h-4" /> Raw SVG endpoint
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function OptionGroup({ label, options, value, onChange, testidPrefix }) {
  return (
    <div data-testid={`group-${testidPrefix}`}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-2">
        {label}
      </div>
      <div className="flex border border-black bg-white">
        {options.map((o, idx) => {
          const active = value === o.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onChange(o.id)}
              className={`flex-1 px-3 py-2.5 text-xs font-mono uppercase tracking-widest transition-colors ${
                idx > 0 ? "border-l border-black" : ""
              } ${
                active
                  ? "bg-[#0A0A0A] text-white"
                  : "bg-white text-[#0A0A0A] hover:bg-[#F1F5F9]"
              }`}
              data-testid={`${testidPrefix}-${o.id}`}
              aria-pressed={active}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
